import clsx from 'clsx';
import React, { useId } from 'react';
import { BorderWidth, Colors, Spacing } from '@common/tokens';
import { Button, Flex, Gap, Icon, Placeholder, Text } from '@common/components';
import { useSideShellContext } from './_side-shell-context';

/**
 * `SideShell.Panel` gives a side-section panel a standard layout: a pinned
 * header (with a built-in back button that pops the SideShell panel stack), a
 * scrollable body, and a pinned footer.
 *
 * Use it as the value of a `panels` entry; the back button only appears when a
 * `header` is given and the panel is rendered inside a SideShell.
 */
export const SideShellPanel = ({
  children,
  header,
  footer,
  hideBackButton = false,
  background,
  className,
  dataTestId,
  loading = false,
}: {
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  hideBackButton?: boolean;
  className?: string;
  dataTestId?: string;
  background?: boolean;
  // While true, the body shows a skeleton placeholder instead of `children`.
  loading?: boolean;
}) => {
  const context = useSideShellContext();
  const showBackButton = !hideBackButton && header !== undefined && !!context;
  const headingId = useId();
  const backgroundColor = background ? Colors.steelLightest : 'transparent';

  return (
    <div
      className={clsx('wk-side-shell-panel', className)}
      data-testid={dataTestId}
      // Expose the panel as a named region so assistive tech can identify and
      // jump to it; only meaningful when there's a title to name it.
      role={header !== undefined ? 'region' : undefined}
      aria-labelledby={header !== undefined ? headingId : undefined}
    >
      {header !== undefined && (
        <header className="wk-side-shell-panel__header">
          <Flex align="center">
            {showBackButton && (
              <Button
                kind="link"
                ariaLabel="Back"
                className="wk-side-shell-panel__back"
                icon={<Icon icon="chevronLeft" kind="solid" size="sm" />}
                onClick={context.goBack}
              />
            )}
            <Text id={headingId} size="lg" weight="medium">
              {header}
            </Text>
          </Flex>
        </header>
      )}

      <div className="wk-side-shell-panel__body">
        {loading ? <PanelLoadingSkeleton /> : children}
      </div>

      {footer !== undefined && (
        <footer className="wk-side-shell-panel__footer">{footer}</footer>
      )}

      <style jsx>{`
        .wk-side-shell-panel {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
        }
        .wk-side-shell-panel :global(.wk-side-shell-panel__header) {
          flex-shrink: 0;
          background: ${Colors.white};
          border-bottom: ${BorderWidth.sm} solid ${Colors.steel};
        }
        .wk-side-shell-panel :global(.wk-side-shell-panel__back) {
          padding: 12px;
        }
        .wk-side-shell-panel__body {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          padding: ${Spacing.md}px;
          background: ${backgroundColor};
        }
        .wk-side-shell-panel__footer {
          flex-shrink: 0;
          padding: ${Spacing.md}px ${Spacing.lg}px;
          background: ${Colors.white};
          border-top: ${BorderWidth.sm} solid ${Colors.steel};
        }
      `}</style>
    </div>
  );
};

// Skeleton shown in the panel body while `loading` is true. Mirrors the common Drawer's
// loading skeleton (a heading line plus a few paragraph rows) so the two side surfaces match.
function PanelLoadingSkeleton() {
  return (
    <Gap vertical size="lg" align="stretch" fullWidth>
      <Placeholder.Input loading size="small" width={200}>
        <div />
      </Placeholder.Input>
      <Placeholder
        loading
        paragraph={{ rows: 3, width: ['100%', '95%', '75%'] }}
      >
        <div />
      </Placeholder>
      <Placeholder.Input loading size="small" width={140}>
        <div />
      </Placeholder.Input>
      <Placeholder loading paragraph={{ rows: 2, width: ['100%', '80%'] }}>
        <div />
      </Placeholder>
    </Gap>
  );
}
